import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Calendar, ShoppingBag, LogOut, Phone, Clock } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import OTPModal from "../components/OTPModal";
import axios from "axios";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending:   { cls: "text-amber-500 bg-amber-500/10 border-amber-500/20",   key: 'status.pending'   },
  confirmed: { cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20", key: 'status.confirmed' },
  cancelled: { cls: "text-red-500 bg-red-500/10 border-red-500/20",         key: 'status.cancelled' },
};

function StatusBadge({ status }) {
  const { t } = useLang();
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s.cls}`}>{t(s.key)}</span>
  );
}

export default function MyOrdersPage() {
  const { customer, setVerified, logout, customerHeader } = useCustomerAuth();
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [showOTP, setShowOTP] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (customer?.token) fetchOrders();
  }, [customer]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/orders`, customerHeader());
      setOrders(res.data);
    } catch {
      setOrders({ preorders: [], orders: [] });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (phone.trim().length === 10) setShowOTP(true);
  };

  const allItems = orders
    ? [
        ...(orders.preorders || []).map(p => ({ ...p, type: "preorder" })),
        ...(orders.orders || []).map(o => ({ ...o, type: "order" })),
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--c-bg)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight" style={{ color: 'var(--c-text)' }}>
            {t('myorders.title')}
          </h1>
          {customer ? (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm flex items-center gap-1.5" style={{ color: 'var(--c-muted)' }}>
                <Phone className="w-3.5 h-3.5" /> +91 {customer.phone}
                {customer.name && <span className="ml-1">· {customer.name}</span>}
              </p>
              <button onClick={logout} data-testid="customer-logout-btn"
                className="text-xs flex items-center gap-1 transition-colors hover:text-red-500"
                style={{ color: 'var(--c-muted)' }}>
                <LogOut className="w-3.5 h-3.5" /> {t('myorders.signOut')}
              </button>
            </div>
          ) : (
            <p className="text-sm mt-2" style={{ color: 'var(--c-muted)' }}>
              {t('myorders.subtitle')}
            </p>
          )}
        </motion.div>

        {/* Not logged in */}
        {!customer && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-8 rounded-2xl border text-center" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(217,122,70,0.1)' }}>
              <Package className="w-8 h-8" style={{ color: '#D97A46' }} />
            </div>
            <h2 className="font-serif text-xl font-bold mb-2" style={{ color: 'var(--c-text)' }}>{t('myorders.trackTitle')}</h2>
            <p className="text-sm mb-6 max-w-xs mx-auto" style={{ color: 'var(--c-muted)' }}>
              {t('myorders.trackSubtext')}
            </p>
            <form onSubmit={handlePhoneSubmit} className="max-w-xs mx-auto space-y-3">
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl border" style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
                <span className="text-sm font-medium" style={{ color: 'var(--c-muted)' }}>+91</span>
                <input
                  type="tel"
                  maxLength={10}
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder={t('myorders.phonePlaceholder')}
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                  style={{ color: 'var(--c-text)' }}
                  data-testid="my-orders-phone-input"
                />
              </div>
              <button type="submit" disabled={phone.length !== 10}
                data-testid="my-orders-verify-btn"
                className="w-full py-3 rounded-full font-semibold text-white text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                style={{ backgroundColor: '#D97A46' }}>
                {t('myorders.verifyBtn')}
              </button>
            </form>
          </motion.div>
        )}

        {/* Loading */}
        {customer && loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 rounded-2xl border animate-pulse" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }} />
            ))}
          </div>
        )}

        {/* Orders list */}
        {customer && !loading && orders && (
          <>
            {allItems.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="p-12 rounded-2xl border text-center" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}>
                <ShoppingBag className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--c-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('myorders.noOrders')}</p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm font-medium" style={{ color: 'var(--c-muted)' }}>
                  {allItems.length === 1 ? t('myorders.found', { n: allItems.length }) : t('myorders.foundPlural', { n: allItems.length })}
                </p>
                {allItems.map((item, i) => (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    className="p-5 rounded-2xl border" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}
                    data-testid={`order-card-${item.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {item.type === "preorder"
                          ? <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: '#D97A46' }} />
                          : <ShoppingBag className="w-4 h-4 flex-shrink-0" style={{ color: '#D97A46' }} />
                        }
                        <span className="text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color: '#D97A46', borderColor: 'rgba(217,122,70,0.3)', backgroundColor: 'rgba(217,122,70,0.08)' }}>
                          {item.type === "preorder" ? t('myorders.preorder') : t('myorders.order')}
                        </span>
                        {item.id && (
                          <span className="font-mono text-xs font-medium px-2 py-0.5 rounded-md" style={{ color: 'var(--c-muted)', backgroundColor: 'var(--c-bg)' }}>
                            #{item.id.slice(0, 8).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <StatusBadge status={item.status} />
                    </div>

                    {item.type === "preorder" ? (
                      <div>
                        <p className="font-semibold text-sm" style={{ color: 'var(--c-text)' }}>{item.mushroomName}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--c-muted)' }}>{t('myorders.orderedBy')}: {item.customerName}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--c-muted)' }}>{t('myorders.qty')}: {item.qty}</p>
                        {item.address && <p className="text-xs mt-0.5" style={{ color: 'var(--c-muted)' }}>{item.address}</p>}
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-medium mb-1" style={{ color: 'var(--c-muted)' }}>{t('myorders.orderedBy')}: {item.customer_name}</p>
                        <div className="space-y-1">
                          {(item.items || []).map((it) => (
                            <p key={`${it.name}-${it.uom}`} className="text-sm" style={{ color: 'var(--c-text)' }}>
                              {it.name} ({it.uom}) × {it.qty}
                              <span className="ml-1.5 text-xs" style={{ color: 'var(--c-muted)' }}>Rs.{it.price * it.qty}</span>
                            </p>
                          ))}
                        </div>
                        <p className="font-semibold text-sm mt-2" style={{ color: '#D97A46' }}>{t('myorders.total')}: Rs.{item.total}</p>
                        {item.address && <p className="text-xs mt-1" style={{ color: 'var(--c-muted)' }}>{item.address}</p>}
                      </div>
                    )}

                    <p className="text-xs mt-3 flex items-center gap-1" style={{ color: 'var(--c-muted)' }}>
                      <Clock className="w-3 h-3" />
                      {new Date(item.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}, {new Date(item.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* OTP Modal */}
      {showOTP && (
        <OTPModal
          phone={phone}
          name=""
          onVerified={(ph, name, token) => {
            setVerified(ph, name, token);
            setShowOTP(false);
          }}
          onClose={() => setShowOTP(false)}
        />
      )}
    </div>
  );
}
